/**
 * Site weather: the pour's weather station and its newest reading, shown
 * once per pour. A weather station is picked on the Hardware step but is no
 * monitoring location: one station feeds the whole pour (its readings are
 * kept per pour), so it never appears in a location list or in Location
 * Details. Its history is the Weather Data section.
 *
 *  - SiteWeatherLine  one row above the location rows (control panel)
 *  - SiteWeatherCard  a one-row table above the location table (dashboard),
 *                     laid out like it; a card like the location cards on
 *                     the phone (compact)
 *
 * Only a linked station is shown (utils/siteConditions): a station that is
 * not the pour's Weather Station, or a pour with neither Onsite Weather
 * Conditions nor Concrete Evaporation Rate on, gets no readings, so both
 * render nothing.
 *
 * Freshness follows the shared rule with the weather-station bands
 * (utils/onlineStatus): Current, Delayed, then Offline after 60 minutes, or
 * Scheduled / awaiting its first reading. Past Offline the values are
 * greyed, so an old reading never reads as "now". Humidity carries the dew
 * point. With the station's readings (the card, given a pour id) the card
 * also shows the trend over the last hour, the wind peak in the last hour
 * and the last 24 hours of air temperature. Wind arrives in km/h and shows
 * in the company's unit.
 */
import React, { useMemo } from 'react';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Paper,
  Typography,
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  ArrowForward as ArrowForwardIcon,
  CloudOutlined as CloudIcon,
  CloudOffOutlined as CloudOffIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import type { SiteWeather } from '../../services/pours';
import { formatDateTimeWithZone, formatInTimeZone, formatTimeWithZone, parseUtc } from '../../utils/datetime';
import { getOnlineStatus, pourStatusEnded, type OnlineStatus } from '../../utils/onlineStatus';
import { dewPoint, siteConditions, type SiteConditions } from '../../utils/siteConditions';
import { formatWind, type WindSpeedUnit } from '../../utils/windSpeed';
import { SERIES_COLORS } from './overlayModel';
import { useWeatherPoints } from './useWeatherPoints';

const FRESHNESS_COLORS: Record<OnlineStatus['level'], string> = {
  online: '#2e7d32',
  delayed: '#9a6a00',
  offline: '#A32D2D',
  ended: '#757575',
  scheduled: '#185FA5',
};

interface SiteWeatherProps {
  site: SiteWeather;
  /** The pour's status code: a completed pour's station shows Logging ended. */
  pourStatus?: string | null;
  windSpeedUnit?: WindSpeedUnit;
  timezoneIana?: string | null;
  /** Injectable for tests. */
  now?: number;
}

/** The station's freshness by the weather-station bands. */
export const siteWeatherStatus = (
  site: SiteWeather,
  pourStatus?: string | null,
  now: number = Date.now(),
): OnlineStatus => {
  const endsAt = parseUtc(site.logging_ended).getTime();
  return getOnlineStatus(site.at, now, {
    weatherStation: true,
    startsAt: site.starts_at,
    ended: pourStatusEnded(pourStatus) || (!Number.isNaN(endsAt) && endsAt <= now),
  });
};

/** "Weather Station 1 · A4100972", or the code alone. */
const stationLabel = (site: SiteWeather): string =>
  [site.name, site.code].filter(Boolean).join(' · ');

/** What the freshness line says after its label. */
const freshnessDetail = (
  site: SiteWeather,
  status: OnlineStatus,
  formatTime: (v: string) => string,
): string | null => {
  if (status.level === 'scheduled') return site.starts_at ? `Starts ${formatTime(site.starts_at)}` : null;
  if (status.awaiting) return site.starts_at ? `Started ${formatTime(site.starts_at)}` : null;
  if (!site.at) return status.level === 'ended' ? null : 'No readings received';
  return `Last read ${formatTime(site.at)}`;
};

const temp = (v?: number | null) => (v != null ? `${v.toFixed(1)} °C` : '-');
const humidity = (v?: number | null) => (v != null ? `${v.toFixed(0)} %RH` : '-');
const wind = (v: number | null | undefined, unit?: WindSpeedUnit) => (v != null ? formatWind(v, unit) : '-');

/** "Last read 04:55" with a small clock, coloured by freshness: the same
 *  line the location rows use. */
const FreshnessLine: React.FC<{ status: OnlineStatus; detail: string | null }> = ({ status, detail }) => {
  const color = FRESHNESS_COLORS[status.level];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <ClockIcon sx={{ fontSize: 13, color, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color, fontSize: '0.7rem' }}>
        {status.label}{detail ? ` · ${detail}` : ''}
      </Typography>
    </Box>
  );
};

/** The control panel's one row of site weather, above the location rows. */
export const SiteWeatherLine: React.FC<SiteWeatherProps> = ({
  site, pourStatus, windSpeedUnit, timezoneIana, now = Date.now(),
}) => {
  if (!site.linked) return null;
  const status = siteWeatherStatus(site, pourStatus, now);
  const offline = status.level === 'offline';
  const detail = freshnessDetail(site, status, (v) => formatTimeWithZone(v, timezoneIana));
  const dew = dewPoint(site.temp, site.humidity);
  const parts = [
    { key: 'air', label: 'Air temp', value: temp(site.temp), sub: site.max_temp != null ? `max ${site.max_temp.toFixed(1)} °C` : undefined },
    { key: 'rh', label: 'Humidity', value: humidity(site.humidity), sub: dew != null ? `dew point ${dew.toFixed(1)} °C` : undefined },
    { key: 'wind', label: 'Wind', value: wind(site.wind_speed, windSpeedUnit) },
  ];
  return (
    <Box
      data-testid="site-weather-line"
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 0.5,
        px: 1.25, py: 0.75, my: 0.75, borderRadius: 1, border: '1px solid',
        bgcolor: offline ? '#FFF8E6' : '#F4F8FD',
        borderColor: offline ? '#F2D9A6' : '#D6E6F8',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {offline
          ? <CloudOffIcon sx={{ fontSize: 17, color: FRESHNESS_COLORS.delayed }} />
          : <CloudIcon sx={{ fontSize: 17, color: 'text.secondary' }} />}
        <Typography variant="body2" fontWeight={600} fontSize="0.82rem">Site weather</Typography>
        {stationLabel(site) && (
          <Typography variant="caption" color="text.secondary" fontSize="0.72rem">{stationLabel(site)}</Typography>
        )}
      </Box>
      {parts.map((p) => (
        <Typography
          key={p.key}
          variant="body2"
          fontSize="0.8rem"
          noWrap
          sx={{ color: offline ? 'text.disabled' : 'text.primary' }}
        >
          <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>{p.label}</Box>
          <Box component="span" sx={{ fontWeight: 600 }}>{p.value}</Box>
          {p.sub && <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>({p.sub})</Box>}
        </Typography>
      ))}
      <Box sx={{ ml: 'auto' }}>
        <FreshnessLine status={status} detail={detail} />
      </Box>
    </Box>
  );
};

/** "+0.2 °C in the last hour" with the location table's trending icon. */
const TrendLine: React.FC<{ trend: number }> = ({ trend }) => {
  const text = `${trend >= 0 ? '+' : '−'}${Math.abs(trend).toFixed(1)} °C in the last hour`;
  const Icon = trend >= 0.3 ? TrendingUpIcon : trend <= -0.3 ? TrendingDownIcon : TrendingFlatIcon;
  return (
    <Box data-testid="site-weather-trend" aria-label={text} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Icon fontSize="small" color="action" />
      <Typography variant="caption" color="text.secondary">{text}</Typography>
    </Box>
  );
};

/** Where the air temperature sits between its 24-hour low and high. */
const RangeBar: React.FC<{ low: number; high: number; value: number }> = ({ low, high, value }) => {
  const at = high > low ? Math.min(100, Math.max(0, ((value - low) / (high - low)) * 100)) : 50;
  return (
    <Box data-testid="site-weather-range" sx={{ mt: 0.5 }}>
      <Box sx={{ position: 'relative', height: 6, borderRadius: 1, bgcolor: 'grey.200' }}>
        <Box sx={{ position: 'absolute', top: -3, left: `calc(${at}% - 1px)`, width: 2, height: 12, borderRadius: 0.5, bgcolor: SERIES_COLORS[0] }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">24 h low {low.toFixed(1)} °C</Typography>
        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">High {high.toFixed(1)} °C</Typography>
      </Box>
    </Box>
  );
};

/** Relative humidity as a thin 0-100 % bar, coloured like the location
 *  table's humidity bar. */
const HumidityBar: React.FC<{ value: number }> = ({ value }) => (
  <Box sx={{ mt: 0.75, height: 6, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
    <Box sx={{ height: '100%', width: `${Math.min(Math.max(value, 0), 100)}%`, bgcolor: '#4a90c2' }} />
  </Box>
);

/** At least this many °C top to bottom, so a still day reads flat instead
 *  of a fraction of a degree filling the chart. */
const TRACE_MIN_SPAN = 2;
const TRACE_HEIGHT = 72;

/** The last 24 hours of air temperature: a small line chart with its high
 *  and low on the left and its first and last reading times below. */
const Trace: React.FC<{ trace: SiteConditions['trace']; timezoneIana?: string | null }> = ({ trace, timezoneIana }) => {
  const W = 600;
  const H = TRACE_HEIGHT;
  const t0 = trace[0][0];
  const t1 = trace[trace.length - 1][0];
  const values = trace.map((p) => p[1]);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const mid = (lo + hi) / 2;
  const span = Math.max(hi - lo, TRACE_MIN_SPAN);
  const x = (t: number) => ((t - t0) / (t1 - t0 || 1)) * W;
  const y = (v: number) => 6 + (1 - (v - (mid - span / 2)) / span) * (H - 12);
  const line = trace.map((p, i) => `${i ? 'L' : 'M'}${x(p[0]).toFixed(1)} ${y(p[1]).toFixed(1)}`).join('');
  // "01 Nov, 03:45": the ends of the window are a day apart, so each names its day.
  const time = (ms: number) => formatInTimeZone(new Date(ms).toISOString().slice(0, 19), timezoneIana, { month: 'short', year: undefined });
  const label = { fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1 } as const;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 0.75 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: H, textAlign: 'right', py: '2px' }}>
        <Typography sx={label}>{hi.toFixed(1)} °C</Typography>
        <Typography sx={label}>{lo.toFixed(1)} °C</Typography>
      </Box>
      <Box sx={{ borderLeft: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        <svg
          data-testid="site-weather-trace"
          width="100%"
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{ display: 'block' }}
        >
          <line x1={0} x2={W} y1={y(hi)} y2={y(hi)} stroke="#e0e0e0" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
          <line x1={0} x2={W} y1={y(lo)} y2={y(lo)} stroke="#e0e0e0" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
          <path d={`${line}L${W} ${H}L0 ${H}Z`} fill={SERIES_COLORS[0]} fillOpacity={0.08} />
          <path d={line} fill="none" stroke={SERIES_COLORS[0]} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        </svg>
      </Box>
      <Box />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography sx={label}>{time(t0)}</Typography>
        <Typography sx={label}>{time(t1)}</Typography>
      </Box>
    </Box>
  );
};

interface SiteWeatherCardProps extends SiteWeatherProps {
  /** The pour whose station readings add the trend, wind peak and last 24
   *  hours; without it the card shows the newest reading only. */
  pourId?: string | null;
  /** Bumped by the page on each refresh: the newer readings are fetched. */
  refreshSignal?: number;
  /** Opens the Weather Data section (the station's history). */
  onOpenWeatherData?: () => void;
  /** The phone layout: a card like the location cards, no section link. */
  compact?: boolean;
}

/** The dashboard's site weather, above the location table. */
export const SiteWeatherCard: React.FC<SiteWeatherCardProps> = ({
  site, pourStatus, windSpeedUnit, timezoneIana, now = Date.now(), pourId, refreshSignal, onOpenWeatherData, compact = false,
}) => {
  const { points } = useWeatherPoints(site.linked ? pourId : null, refreshSignal);
  const conditions = useMemo(() => (points.length > 0 ? siteConditions(points) : undefined), [points]);
  if (!site.linked) return null;

  const status = siteWeatherStatus(site, pourStatus, now);
  const offline = status.level === 'offline';
  const detail = freshnessDetail(site, status, (v) => formatDateTimeWithZone(v, timezoneIana));
  const dew = dewPoint(site.temp, site.humidity);
  const valueColor = offline ? 'text.disabled' : 'text.primary';
  const stationIcon = offline
    ? <CloudOffIcon sx={{ fontSize: 16, color: FRESHNESS_COLORS.offline }} />
    : <CloudIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;

  if (compact) {
    const tiles = [
      { key: 'air', label: 'Air temp', value: temp(site.temp), caption: site.max_temp != null ? `Max ${site.max_temp.toFixed(1)} °C` : null },
      { key: 'rh', label: 'Humidity', value: humidity(site.humidity), caption: dew != null ? `Dew point ${dew.toFixed(1)} °C` : null },
      { key: 'wind', label: 'Wind', value: wind(site.wind_speed, windSpeedUnit), caption: null },
    ];
    return (
      <Card variant="outlined" data-testid="site-weather-card" sx={{ borderRadius: 2, mb: 1.5, minWidth: 0 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {stationIcon}
              <Typography noWrap sx={{ fontSize: '1rem', fontWeight: 600 }}>Site weather</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>{stationLabel(site)}</Typography>
            <FreshnessLine status={status} detail={detail} />
          </Box>
          {status.level !== 'scheduled' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
              {tiles.map((tile) => (
                <Box key={tile.key} data-testid={`site-weather-${tile.key}`} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>{tile.label}</Typography>
                  <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25, color: valueColor }}>{tile.value}</Typography>
                  {tile.caption && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>{tile.caption}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // The location table's look (grey header band, sentence-case labels,
  // bold values with small captions), laid out as tiles so each reading
  // keeps its detail.
  const labelSx = { fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 } as const;
  const captionSx = { fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 } as const;
  const tileSx = {
    px: 2, py: 1.5, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25,
    borderLeft: { xs: 0, sm: '1px solid' }, borderTop: { xs: '1px solid', sm: 0 }, borderColor: 'divider',
  } as const;
  const scheduled = status.level === 'scheduled';
  const trend = scheduled ? null : conditions?.trend1h;
  const showRange = !scheduled && conditions?.low24h != null && conditions.high24h != null && site.temp != null;

  return (
    <Paper elevation={2} data-testid="site-weather-card" sx={{ mb: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 0.25,
          px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {stationIcon}
          <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Site Weather</Typography>
        </Box>
        <Typography variant="body2" fontWeight={600} fontSize="0.85rem" noWrap>
          {stationLabel(site)}
        </Typography>
        <FreshnessLine status={status} detail={detail} />
        {onOpenWeatherData && (
          <ButtonBase
            onClick={onOpenWeatherData}
            sx={{ ml: 'auto', gap: 0.5, color: 'primary.main', fontSize: '0.75rem', fontWeight: 600, borderRadius: 1, px: 0.5 }}
          >
            Weather Data <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </ButtonBase>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: 'minmax(190px, 1.15fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) minmax(240px, 2.2fr)',
          },
        }}
      >
        <Box data-testid="site-weather-air" sx={{ ...tileSx, borderLeft: 0, borderTop: 0 }}>
          <Typography sx={labelSx}>Air temp</Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.15, color: valueColor }}>
            {scheduled ? '-' : temp(site.temp)}
          </Typography>
          {trend != null ? (
            <TrendLine trend={trend} />
          ) : (
            !scheduled && site.max_temp != null && <Typography sx={captionSx}>Max {site.max_temp.toFixed(1)} °C</Typography>
          )}
          {showRange && <RangeBar low={conditions!.low24h!} high={conditions!.high24h!} value={site.temp!} />}
        </Box>
        <Box data-testid="site-weather-rh" sx={tileSx}>
          <Typography sx={labelSx}>Humidity</Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3, color: valueColor }}>
            {scheduled ? '-' : humidity(site.humidity)}
          </Typography>
          {!scheduled && dew != null && <Typography sx={captionSx}>Dew point {dew.toFixed(1)} °C</Typography>}
          {!scheduled && site.humidity != null && <HumidityBar value={site.humidity} />}
        </Box>
        <Box data-testid="site-weather-wind" sx={tileSx}>
          <Typography sx={labelSx}>Wind</Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3, color: valueColor }}>
            {scheduled ? '-' : wind(site.wind_speed, windSpeedUnit)}
          </Typography>
          {!scheduled && conditions?.windPeak1h != null && (
            <Typography sx={captionSx}>Peak {formatWind(conditions.windPeak1h, windSpeedUnit)} in the last hour</Typography>
          )}
        </Box>
        <Box
          sx={{
            ...tileSx,
            gridColumn: { sm: '1 / -1', md: 'auto' },
            borderLeft: { xs: 0, md: '1px solid' },
            borderTop: { xs: '1px solid', md: 0 },
          }}
        >
          <Typography sx={labelSx}>Air temp, last 24 hours</Typography>
          {!scheduled && conditions && conditions.trace.length >= 2 ? (
            <Box sx={{ mt: 0.5 }}>
              <Trace trace={conditions.trace} timezoneIana={timezoneIana} />
            </Box>
          ) : (
            <Typography sx={captionSx}>{scheduled ? 'Not started' : 'No history yet'}</Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
